<h2 mat-dialog-title>
  {{ isReversal ? 'Reverse a balance entry' : 'Post to customer balance' }}
</h2>

<mat-dialog-content class="balance-entry-dialog__content">
  <p class="balance-entry-dialog__customer">
    <mat-icon aria-hidden="true">account_circle</mat-icon>
    <span>{{ data.customerName || data.customerId }}</span>
  </p>

  @if (isReversal) {
    <!--
      A reversal compensates: FTE writes an opposite entry and stamps the original reversed.
      Nothing is edited or deleted, so the statement still shows both — which is why the reason
      is required here too.
    -->
    <div class="balance-entry-dialog__reversing">
      <p>
        Reversing <strong>{{ data.reverse?.ledgerId }}</strong> — {{ data.reverse?.amount | currency }}
      </p>
      @if (data.reverse?.reason) {
        <p class="balance-entry-dialog__original-reason">"{{ data.reverse?.reason }}"</p>
      }
      <p class="balance-entry-dialog__hint">
        A compensating entry is written and the original is stamped reversed. Neither is edited or
        deleted, so the customer's statement still shows what happened.
      </p>
    </div>
  } @else {
    <mat-form-field appearance="outline" class="balance-entry-dialog__field">
      <mat-label>Type</mat-label>
      <mat-select [(ngModel)]="entryType" name="entryType">
        @for (t of entryTypes; track t.entryType) {
          <mat-option [value]="t.entryType">{{ t.label }}</mat-option>
        }
      </mat-select>
    </mat-form-field>

    @if (needsDirection) {
      <mat-form-field appearance="outline" class="balance-entry-dialog__field">
        <mat-label>Direction</mat-label>
        <mat-select [(ngModel)]="adjustmentDirection" name="adjustmentDirection">
          <mat-option value="charge">Charge — raises what the customer owes</mat-option>
          <mat-option value="credit">Credit — lowers what the customer owes</mat-option>
        </mat-select>
      </mat-form-field>
    }

    <mat-form-field appearance="outline" class="balance-entry-dialog__field">
      <mat-label>Amount</mat-label>
      <span matTextPrefix>$&nbsp;</span>
      <input
        matInput
        type="text"
        name="amount"
        [(ngModel)]="amount"
        currencyMask
        [options]="{ prefix: '', thousands: ',', decimal: '.', precision: 2, min: 0, allowNegative: false }"
      />
      @if (effectLabel) {
        <mat-hint>{{ effectLabel }}</mat-hint>
      }
    </mat-form-field>

    <mat-form-field appearance="outline" class="balance-entry-dialog__field">
      <mat-label>Reference (optional)</mat-label>
      <input matInput name="reference" [(ngModel)]="reference" maxlength="100" />
      <mat-hint>A ticket, cheque number or credit note — whatever ties this back to the decision.</mat-hint>
    </mat-form-field>
  }

  <mat-form-field appearance="outline" class="balance-entry-dialog__field">
    <mat-label>Reason</mat-label>
    <textarea matInput name="reason" [(ngModel)]="reason" rows="3" maxlength="500"></textarea>
    <mat-hint>
      Required. It is stored on the ledger entry and is the only record of why the balance moved.
    </mat-hint>
  </mat-form-field>

  <!--
    There is no invoice behind this, so it is worth saying so plainly: the money is settled by a
    payment to the account, not against a document.
  -->
  @if (!isReversal) {
    <p class="balance-entry-dialog__note">
      <mat-icon aria-hidden="true">info</mat-icon>
      <span>
        No invoice or credit memo is raised. A charge is settled by a payment to the account; a
        credit can be applied to the customer's invoices in Collect Payment.
      </span>
    </p>
  }

  @if (errorMessage) {
    <p class="balance-entry-dialog__error" role="alert">
      <mat-icon aria-hidden="true">error_outline</mat-icon>
      <span>{{ errorMessage }}</span>
    </p>
  }
</mat-dialog-content>

<mat-dialog-actions align="end">
  <button mat-button type="button" [disabled]="isSaving" (click)="dialogRef.close()">Cancel</button>
  <button mat-flat-button color="primary" type="button" [disabled]="!canSubmit" (click)="submit()">
    @if (isSaving) {
      <mat-spinner diameter="18"></mat-spinner>
    }
    {{ isReversal ? 'Reverse entry' : 'Post entry' }}
  </button>
</mat-dialog-actions>
